import { useState } from 'react';
import Avatar from '@/components/base/Avatar';
import type { AuditCategory, AuditEvent } from '@/types/portal';
import { clockOf, type AuditDayGroup } from '@/utils/audit';
import AuditEventRow from './AuditEventRow';
import { CATEGORY_LOOK } from './auditLook';

interface AuditTimelineProps {
  groups: AuditDayGroup[];
  onSelect: (event: AuditEvent) => void;
}

/** Actions by one person this close together read as one burst. */
const BURST_GAP_MS = 15 * 60 * 1000;
/** A burst shows this many actions before "Show N more". */
const BURST_PREVIEW = 3;

interface Burst {
  key: string;
  actorName: string;
  actorAvatar: string;
  actorRole: string;
  events: AuditEvent[];
}

/** Consecutive events (newest first) by the same person, each within 15 minutes of the last. */
function toBursts(events: AuditEvent[]): Burst[] {
  const bursts: Burst[] = [];
  for (const event of events) {
    const last = bursts.at(-1);
    const previous = last?.events.at(-1);
    if (
      last &&
      previous &&
      previous.actorId === event.actorId &&
      Date.parse(previous.timestamp) - Date.parse(event.timestamp) <= BURST_GAP_MS
    ) {
      last.events.push(event);
    } else {
      bursts.push({
        key: event.id,
        actorName: event.actorName,
        actorAvatar: event.actorAvatar,
        actorRole: event.actorRole,
        events: [event],
      });
    }
  }
  return bursts;
}

/** "Today", "Yesterday", "3 days ago", "2 weeks ago"… from the real date, not the newest event. */
function relativeDay(dayKey: string) {
  const day = new Date(`${dayKey}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} ${Math.floor(days / 7) === 1 ? 'week' : 'weeks'} ago`;
  const months = Math.floor(days / 30);
  return `${months} ${months === 1 ? 'month' : 'months'} ago`;
}

/** "19 Oct 2026" for a YYYY-MM-DD key. */
function longDate(dayKey: string) {
  return new Date(`${dayKey}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const dayAnchor = (key: string) => `audit-day-${key}`;

export default function AuditTimeline({ groups, onSelect }: AuditTimelineProps) {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-history-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">No matching activity</h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Nothing lines up with this filter. Try another category, actor or impact level, or clear the search.
        </p>
      </div>
    );
  }

  return (
    <div>
      {groups.length > 1 ? <ActivityStrip groups={groups} /> : null}
      <div className="space-y-6">
        {groups.map((group) => (
          <DaySection key={group.key} group={group} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

/**
 * One bar per day with activity, oldest on the left, taller for busier days. Clicking a bar jumps
 * to that day in the timeline.
 */
function ActivityStrip({ groups }: { groups: AuditDayGroup[] }) {
  const days = [...groups].reverse();
  const busiest = Math.max(...days.map((day) => day.count));
  const jump = (key: string) =>
    document.getElementById(dayAnchor(key))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <section className="mb-5 rounded-lg border border-background-200 bg-background-50 px-4 pb-3 pt-4" aria-label="Activity by day">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-500">Activity by day</h3>
        <span className="text-[11px] text-foreground-400">Click a day to jump to it</span>
      </div>
      <div className="flex h-16 items-end gap-1">
        {days.map((day, index) => (
          <button
            key={day.key}
            type="button"
            onClick={() => jump(day.key)}
            title={`${longDate(day.key)} · ${day.count} ${day.count === 1 ? 'event' : 'events'}`}
            aria-label={`Jump to ${longDate(day.key)}, ${day.count} events`}
            className="group flex h-full min-w-[6px] flex-1 cursor-pointer items-end"
          >
            <span
              className="audit-bar w-full rounded-t-sm bg-primary-300 transition-colors group-hover:bg-primary-600"
              style={{ height: `${Math.max(8, (day.count / busiest) * 100)}%`, animationDelay: `${Math.min(index, 30) * 25}ms` }}
            />
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-foreground-400">
        <span>{longDate(days[0].key)}</span>
        <span>{longDate(days[days.length - 1].key)}</span>
      </div>
    </section>
  );
}

/** A day: a header that stays pinned while you scroll through it, then the bursts on a spine. */
function DaySection({ group, onSelect }: { group: AuditDayGroup; onSelect: (event: AuditEvent) => void }) {
  const bursts = toBursts(group.events);
  const byCategory = group.events.reduce<Partial<Record<AuditCategory, number>>>((acc, event) => {
    acc[event.category] = (acc[event.category] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section id={dayAnchor(group.key)} className="scroll-mt-24">
      <header className="sticky top-16 z-10 -mx-1 mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-background-100/90 px-1 py-2 backdrop-blur">
        <div className="flex items-baseline gap-2">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">{relativeDay(group.key)}</h3>
          <span className="text-xs text-foreground-500">
            {longDate(group.key)} · {group.count} {group.count === 1 ? 'event' : 'events'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {(Object.keys(byCategory) as AuditCategory[]).map((category) => (
            <span
              key={category}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${CATEGORY_LOOK[category].chip}`}
              title={`${byCategory[category]} ${category} ${byCategory[category] === 1 ? 'event' : 'events'}`}
            >
              <i className={CATEGORY_LOOK[category].icon} aria-hidden="true" />
              {byCategory[category]}
            </span>
          ))}
        </div>
      </header>

      <ol className="relative space-y-4">
        {/* The spine. */}
        <span aria-hidden="true" className="absolute bottom-3 left-[17px] top-3 w-0.5 rounded-full bg-gradient-to-b from-primary-200 via-background-300 to-background-200" />
        {bursts.map((burst, index) => (
          <BurstItem key={burst.key} burst={burst} order={index} onSelect={onSelect} />
        ))}
      </ol>
    </section>
  );
}

/** One person's run of actions: their avatar on the spine, then the actions in a card. */
function BurstItem({ burst, order, onSelect }: { burst: Burst; order: number; onSelect: (event: AuditEvent) => void }) {
  const [open, setOpen] = useState(false);
  const shown = open ? burst.events : burst.events.slice(0, BURST_PREVIEW);
  const hidden = burst.events.length - shown.length;
  const newest = clockOf(burst.events[0].timestamp);
  const oldest = clockOf(burst.events[burst.events.length - 1].timestamp);
  const needsLook = burst.events.some((event) => event.outcome !== 'Success' || event.impact !== 'Standard');

  return (
    <li className="audit-burst relative flex gap-3" style={{ animationDelay: `${Math.min(order, 12) * 45}ms` }}>
      <span className={`relative z-[1] mt-0.5 shrink-0 rounded-full ring-4 ${needsLook ? 'ring-secondary-100' : 'ring-background-100'}`}>
        <Avatar src={burst.actorAvatar} alt={burst.actorName} size="sm" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-semibold text-foreground-950">{burst.actorName}</span>
          {burst.actorRole ? <span className="text-xs text-foreground-500">{burst.actorRole}</span> : null}
          <span className="text-xs tabular-nums text-foreground-400">
            {newest === oldest ? newest : `${oldest}–${newest}`}
            {burst.events.length > 1 ? ` · ${burst.events.length} actions` : ''}
          </span>
        </div>
        <ul className="mt-1.5 divide-y divide-background-100 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-sm transition-shadow hover:shadow-md">
          {shown.map((event) => (
            <AuditEventRow key={event.id} event={event} onSelect={onSelect} />
          ))}
          {hidden > 0 || open ? (
            <li>
              <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                className="flex w-full cursor-pointer items-center justify-center gap-1 px-4 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" />
                {open ? 'Show fewer' : `Show ${hidden} more from ${burst.actorName.split(' ')[0]}`}
              </button>
            </li>
          ) : null}
        </ul>
      </div>
    </li>
  );
}
